import {
  MasterDataCatalogDefinitionRequest,
  MasterDataCatalogDefinitionResponse,
  MasterDataCatalogItemRequest,
  MasterDataCatalogItemResponse,
  MasterDataCustomerRequest,
  MasterDataCustomerResponse,
  MasterDataCurrencyResponse,
  MasterDataCurrencyRequest,
  MasterDataCountryResponse,
  MasterDataTaxUnitRequest,
  MasterDataTaxUnitResponse,
  MasterDataPensionFundRequest,
  MasterDataPensionFundResponse,
  MasterDataDepartmentResponse,
  MasterDataDistrictResponse,
  MasterDataDocumentTypeResponse,
  MasterDataDocumentTypeRequest,
  MasterDataProvinceResponse,
  MasterDataProductRequest,
  MasterDataProductResponse,
  MasterDataSctrRequest,
  MasterDataSctrResponse,
  MasterDataTaxRateRequest,
  MasterDataTaxRateResponse,
  MasterDataBankEntityRequest,
  MasterDataBankEntityResponse,
  MasterDataWorkerTypeRequest,
  MasterDataWorkerTypeResponse,
  MasterDataSupplierRequest,
  MasterDataSupplierResponse,
  MasterDataSupplierPageRequest,
  MasterDataSupplierListRequest,
  MasterDataSupplierRequirementPageRequest,
  MasterDataSupplierBankAccountPageRequest,
  MasterDataSupplierDetailPageResponse,
  MasterDataSupplierRequirementResponse,
  MasterDataSupplierBankAccountResponse,
  MasterDataSupplierSunatResponse,
  MasterDataSupportDocumentTypeResponse,
} from "lib-klab-sdks"; import { Country } from "../../../domain/models/master-data/reference-data.model";
import {
  Supplier,
  SupplierPageRequest,
  SupplierListRequest,
  SupplierRequirementPageRequest,
  SupplierBankAccountPageRequest,
  SupplierDetailPage,
  SupplierRequirement,
  SupplierBankAccountInfo,
  SupplierSunatResult,
  SupplierRequest,
} from "../../../domain/models/master-data/supplier.model";
import { SupportDocumentType } from "../../../domain/models/master-data/support-document-type.model";
import {
  CustomerUpsertRequest,
  MasterDataCustomer,
  MasterDataProduct,
  ProductUpsertRequest,
} from "../../../domain/models/master-data/business-data.model";
import {
  Currency,
  CurrencyUpsertRequest,
  TaxUnit,
  TaxUnitUpsertRequest,
  PensionFund,
  PensionFundUpsertRequest,
  DynamicCatalog,
  DynamicCatalogItem,
  DynamicCatalogItemUpsertRequest,
  DynamicCatalogUpsertRequest,
  DocumentType,
  DocumentTypeUpsertRequest,
  GeoDepartment,
  GeoDistrict,
  GeoProvince,
  TaxRate,
  TaxRateCreateRequest,
  SctrProvider,
  SctrUpsertRequest,
  BankEntity,
  BankEntityUpsertRequest,
  WorkerType,
  WorkerTypeUpsertRequest,
} from "../../../domain/models/master-data/reference-data.model";

export const mapDocumentType = (
  source: MasterDataDocumentTypeResponse,
): DocumentType => {
  const parts = source.name.split("-");
  const abbreviation = parts.length > 1 ? parts[0] : source.name;
  const cleanName = parts.length > 1 ? parts.slice(1).join("-") : source.name;
  return {
    ...source,
    id: source.id,
    code: source.code ?? abbreviation,
    name: cleanName,
    authorityCode: source.authorityCode ?? null,
    category: source.category ?? "IDENTITY",
    maxLength: source.maxLength ?? null,
    exactLength: source.exactLength ?? false,
    numericOnly: source.numericOnly ?? false,
    active: source.active ?? true,
    origin: source.origin === "NULL" ? null : source.origin,
    countryId: source.countryId,
  };
};

export const mapDocumentTypeRequest = (
  request: DocumentTypeUpsertRequest,
): MasterDataDocumentTypeRequest => ({ ...request });

export const mapCountry = (source: MasterDataCountryResponse): Country => ({
  id: source.id,
  countryDescription: source.countryDescription,
  iso: source.iso,
  niceName: source.niceName,
  iso3: source.iso3,
  numericCode: source.numericCode,
  phoneCode: source.phoneCode,
  symbol: source.symbol,
  courtesy: source.courtesy,
  icon: source.icon
});

export const mapCurrency = (source: MasterDataCurrencyResponse | any): Currency => ({
  ...source,
  idCurrency: Number(source.idCurrency ?? source.id ?? source.id_currency) || 0,
});
export const mapCurrencyRequest = (
  request: CurrencyUpsertRequest,
): MasterDataCurrencyRequest => ({ ...request });

export const mapTaxUnit = (source: MasterDataTaxUnitResponse): TaxUnit => ({
  id: source.taxUnitId,
  startDate: source.startDate,
  endDate: source.endDate,
  acronym: source.acronym,
  description: source.description,
  amount: source.amount,
  active: source.active,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapTaxUnitRequest = (
  request: TaxUnitUpsertRequest,
): MasterDataTaxUnitRequest => ({ ...request });

export const mapPensionFund = (
  source: MasterDataPensionFundResponse,
): PensionFund => ({
  id: source.pensionFundId,
  abbreviation: source.abbreviation,
  name: source.name,
  insuranceCap: source.insuranceCap,
  mandatoryContributionPercent: source.mandatoryContributionPercent,
  disabilityInsurancePercent: source.disabilityInsurancePercent,
  commissionOnRemunerationPercent: source.commissionOnRemunerationPercent,
  mixedCommissionPercent: source.mixedCommissionPercent,
  constructionContributionPercent: source.constructionContributionPercent,
  miningContributionPercent: source.miningContributionPercent,
  afpNetCode: source.afpNetCode,
  accountingAnnex: source.accountingAnnex,
  rtpsCode: source.rtpsCode,
  active: source.active,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapPensionFundRequest = (
  request: PensionFundUpsertRequest,
): MasterDataPensionFundRequest => ({ ...request });

export const mapDepartment = (
  source: MasterDataDepartmentResponse,
): GeoDepartment => ({ ...source });

export const mapProvince = (
  source: MasterDataProvinceResponse,
): GeoProvince => ({ ...source });

export const mapDistrict = (
  source: MasterDataDistrictResponse,
): GeoDistrict => ({ ...source });

export const mapTaxRate = (source: MasterDataTaxRateResponse): TaxRate => ({
  id: source.taxRateId,
  code: source.code,
  name: source.name,
  rate: source.rate,
  effectiveFrom: source.effectiveFrom,
  effectiveTo: source.effectiveTo,
  active: source.active,
  source: source.source,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapTaxRateRequest = (
  request: TaxRateCreateRequest,
): MasterDataTaxRateRequest => ({ ...request });

export const mapCatalog = (
  source: MasterDataCatalogDefinitionResponse,
): DynamicCatalog => ({
  id: source.catalogId,
  code: source.code,
  name: source.name,
  description: source.description,
  metadataSchema: source.metadataSchema,
  active: source.active,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapCatalogRequest = (
  request: DynamicCatalogUpsertRequest,
): MasterDataCatalogDefinitionRequest => ({ ...request });

export const mapCatalogItem = (
  source: MasterDataCatalogItemResponse,
): DynamicCatalogItem => ({
  id: source.itemId,
  catalogId: source.catalogId,
  parentItemId: source.parentItemId,
  code: source.code,
  name: source.name,
  description: source.description,
  metadata: source.metadata,
  sortOrder: source.sortOrder,
  active: source.active,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapCatalogItemRequest = (
  request: DynamicCatalogItemUpsertRequest,
): MasterDataCatalogItemRequest => ({ ...request });

export const mapCustomer = (
  source: MasterDataCustomerResponse,
): MasterDataCustomer => ({
  id: source.customerId,
  companyId: source.companyId,
  code: source.code,
  customerType: source.customerType,
  documentTypeCode: source.documentTypeCode,
  documentNumber: source.documentNumber,
  legalName: source.legalName,
  tradeName: source.tradeName,
  email: source.email,
  phone: source.phone,
  address: source.address,
  districtCode: source.districtCode,
  segmentItemId: source.segmentItemId,
  classification: source.classification,
  active: source.active,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapCustomerRequest = (
  request: CustomerUpsertRequest,
): MasterDataCustomerRequest => ({ ...request });

export const mapProduct = (
  source: MasterDataProductResponse,
): MasterDataProduct => ({
  id: source.productId,
  companyId: source.companyId,
  code: source.code,
  productType: source.productType,
  name: source.name,
  description: source.description,
  categoryItemId: source.categoryItemId,
  unitCode: source.unitCode,
  currencyCode: source.currencyCode,
  unitPrice: source.unitPrice,
  taxCode: source.taxCode,
  active: source.active,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapProductRequest = (
  request: ProductUpsertRequest,
): MasterDataProductRequest => ({ ...request });

export const mapSctr = (source: MasterDataSctrResponse): SctrProvider => ({
  id: source.sctrId,
  name: source.name,
  ruc: source.ruc,
  ratePercent: source.ratePercent,
  insuranceCap: source.insuranceCap,
  type: source.type,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapSctrRequest = (
  request: SctrUpsertRequest,
): MasterDataSctrRequest => ({ ...request });

export const mapBankEntity = (
  source: MasterDataBankEntityResponse,
): BankEntity => ({
  id: source.id,
  abbreviation: source.sourceCode,
  name: source.name,
  type: source.type,
  active: source.active,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const mapBankEntityRequest = (
  request: BankEntityUpsertRequest,
): MasterDataBankEntityRequest => ({ ...request });

export const mapWorkerType = (
  source: MasterDataWorkerTypeResponse,
): WorkerType => ({
  id: source.workerTypeId,
  name: source.name,
  rtpsCode: source.rtpsCode,
  active: source.active,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapWorkerTypeRequest = (
  request: WorkerTypeUpsertRequest,
): MasterDataWorkerTypeRequest => ({ ...request });

export const mapSupplier = (
  source: MasterDataSupplierResponse,
): Supplier => {
  const parts = source.documentType?.split("-") ?? [];
  const documentTypeCode = parts.length > 1 ? parts[0] : source.documentType;
  const documentType = parts.length > 1 ? parts.slice(1).join("-") : source.documentType;
  return {
    ...source,
    bankAccounts: source.bankAccounts?.map(mapSupplierBankAccount) ?? null,
    documentTypeCode,
    documentType,
  };
};

export const mapSupplierPageRequest = (request: SupplierPageRequest): MasterDataSupplierPageRequest => ({
  page: request.page,
  size: request.size,
  search: request.search,
  documentTypeId: request.documentTypeId,
  countryId: request.countryId,
  supplierType: request.supplierType,
});
export const mapSupplierListRequest = (request: SupplierListRequest): MasterDataSupplierListRequest => ({
  active: request.active,
  search: request.search,
  countryId: request.countryId,
});
export const mapSupplierRequirementPageRequest = (request: SupplierRequirementPageRequest): MasterDataSupplierRequirementPageRequest => ({
  page: request.page,
  size: request.size,
  startDate: request.startDate,
  endDate: request.endDate,
  concept: request.concept,
});
export const mapSupplierBankAccountPageRequest = (request: SupplierBankAccountPageRequest): MasterDataSupplierBankAccountPageRequest => ({
  page: request.page,
  size: request.size,
});

export const mapSupplierBankAccount = (source: MasterDataSupplierBankAccountResponse): SupplierBankAccountInfo => ({
  id: source.id,
  supplierId: source.supplierId,
  bankId: source.bankId,
  bankName: source.bankName,
  accountNumber: source.accountNumber,
  cci: source.cci,
  currencyId: source.currencyId,
  currencyCode: source.currencyCode,
  accountHolder: source.accountHolder,
  alias: source.alias,
  active: source.active,
  createdAt: source.createdAt,
  updatedAt: source.updatedAt,
});

export const mapSupplierRequirement = (source: MasterDataSupplierRequirementResponse): SupplierRequirement => ({ ...source });
export const mapSupplierSunatResult = (source: MasterDataSupplierSunatResponse): SupplierSunatResult => ({ ...source });

export function mapSupplierRequirementsPage(
  source: MasterDataSupplierDetailPageResponse<MasterDataSupplierRequirementResponse>,
): SupplierDetailPage<SupplierRequirement> {
  return {
    supplierId: source.supplierId,
    businessName: source.businessName,
    tradeName: source.tradeName,
    documentType: source.documentType,
    documentNumber: source.documentNumber,
    country: source.country,
    supplierType: source.supplierType,
    cards: { ...source.cards },
    content: source.content.map(mapSupplierRequirement),
    page: source.page,
    size: source.size,
    totalElements: source.totalElements,
    totalPages: source.totalPages,
  };
}

export function mapSupplierBankAccountsPage(
  source: MasterDataSupplierDetailPageResponse<MasterDataSupplierBankAccountResponse>,
): SupplierDetailPage<SupplierBankAccountInfo> {
  return {
    supplierId: source.supplierId,
    businessName: source.businessName,
    tradeName: source.tradeName,
    documentType: source.documentType,
    documentNumber: source.documentNumber,
    country: source.country,
    supplierType: source.supplierType,
    cards: { ...source.cards },
    content: source.content.map(mapSupplierBankAccount),
    page: source.page,
    size: source.size,
    totalElements: source.totalElements,
    totalPages: source.totalPages,
  };
}

export const mapSupportDocumentType = (
  source: MasterDataSupportDocumentTypeResponse,
): SupportDocumentType => ({ ...source });

export const mapSupplierRequest = (
  request: SupplierRequest,
): MasterDataSupplierRequest => ({
  ...request,
  bankAccounts: request.bankAccounts?.map((account) => ({ ...account })),
});
